import { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { withALErrorBoundary } from "../../helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";
import { getPreviewParam } from "@components/ProductPage/mto/mtoEngraving";
import ProductAlternativeTextBlockProduct from "./productAlternativeTextBlock/ProductAlternativeTextBlockProduct";

function ProductAlternativeTextBlock({
  pageProductHandle,
  alternatives,
  labelTitle,
  stepPrefix = "",
  componentType,
  tags = [],
  mtoSettings,
}) {
  const [sortedAlternatives, setSortedAlternatives] = useState(alternatives);

  // What the customization is previewing right now, so an alternative opens on the same one. Only a
  // customizable product has settings at all: `null` leaves the preview up to the URL.
  const previewValue = useMemo(
    () => (mtoSettings ? getPreviewParam(mtoSettings) : null),
    [mtoSettings]
  );

  useEffect(() => {
    const sortedAlternativesCopy = [...alternatives];

    const orderIndex = {};
    const CUSTOM_ORDER = [
      // Size example 1
      "Mini",
      "Small",
      "Medium",
      "Large",
      // Size example 2
      "Slim",
      "Bold",
      // Number of stones
      "1 Stone",
      "2 Stones",
      "3 Stones",
      "4 Stones",
      "5 Stones",
      "6 Stones",
      // Fonts
      "MODERN",
      "modern",
      "Script",
      "Signature",
    ];
    CUSTOM_ORDER.forEach((item, index) => {
      orderIndex[item] = index;
    });

    // Order alternatives
    sortedAlternativesCopy.sort((a, b) => {
      const [aLabelName] = a.attrArgs;
      const [bLabelName] = b.attrArgs;
      const indexA = orderIndex[aLabelName];
      const indexB = orderIndex[bLabelName];

      // If neither is in the list, preserve original order
      if (indexA === undefined && indexB === undefined) return 0;
      // Unknown values go to the end
      if (indexA === undefined) return 1;
      if (indexB === undefined) return -1;

      return indexA - indexB;
    });
    setSortedAlternatives(sortedAlternativesCopy);
  }, [alternatives]);

  if (!sortedAlternatives?.length) return null;
  const alternativesToDisplay = sortedAlternatives.filter((a) => a.isFound);

  const isMTO = tags.includes("MTO") && tags.includes("CUSTOMIZABLE");
  const isMTO3 = tags.includes("Collection:September 2026 MTO 3.0");
  const noOfStones = tags.includes("Type:Rings") && !isMTO3 ? "2 Stones" : "3 Stones";

  return (
    <div className={cn("mb-[20px]")} key={labelTitle}>
      <div className="mb-[10px] typo-body font-bold">
        <span className="pr-[10px]">
          {stepPrefix}
          {labelTitle}
        </span>
      </div>
      <div className="flex flex-wrap gap-[10px]">
        {alternativesToDisplay.map((a) => {
          const [labelName] = a.attrArgs;
          const isMostPopular =
            isMTO && a?.attrName === "Number of Stones" && labelName === noOfStones;
          return (
            <ProductAlternativeTextBlockProduct
              key={a.handle}
              isActive={pageProductHandle === a.handle}
              handle={a.handle}
              availableForSale={a.availableForSale}
              activeProductHandle={pageProductHandle}
              labelName={labelName}
              labelTitle={labelTitle}
              fullWidth={alternativesToDisplay.length < 3}
              componentType={componentType}
              isMostPopular={isMostPopular}
              previewValue={previewValue}
            >
              {labelName}
            </ProductAlternativeTextBlockProduct>
          );
        })}
      </div>
    </div>
  );
}

ProductAlternativeTextBlock.propTypes = {
  pageProductHandle: PropTypes.string.isRequired,
  alternatives: PropTypes.arrayOf(
    PropTypes.shape({
      attrArgs: PropTypes.arrayOf(PropTypes.string).isRequired,
      isFound: PropTypes.bool.isRequired,
      handle: PropTypes.string.isRequired,
      attrName: PropTypes.string.isRequired,
      availableForSale: PropTypes.bool.isRequired,
    })
  ),
  labelTitle: PropTypes.string.isRequired,
  stepPrefix: PropTypes.string,
  componentType: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  mtoSettings: PropTypes.objectOf(
    PropTypes.shape({
      variants: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          isSelected: PropTypes.bool.isRequired,
        })
      ),
    })
  ),
};

export default withALErrorBoundary({
  name: "ProductAlternativeTextBlock",
  priority: "P2",
})(ProductAlternativeTextBlock);
